import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { cloneDeep, debounce, isEqual } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../../../redux/slices/treeSlice";

//data depot redux format example:
// dataDepots: {
// 	"383a2d7be5f00ce646f426c7ac69ac1a": {
// 		"name": "dataDepotName",
// 		"fieldList": {
// 			"20fd276b7c4f4e7740638f01eddc8ae2": {
// 				"data_type": "ALPHA",
// 				"name": "ddFieldName"
// 			},
// 			"20fd276b7c4f4e7740638f01eddc8ae2": {
// 				"data_type": "ALPHA",
// 				"name": "ddFieldName2"
// 			}
// 		}
// 	},
// 	"fg4gy8o7why09drqewgfrf8u0r4": {
// 		"name": "dataDepotName2",
// 		"fieldList": {
// 			"8d842275e00f78cca15d7181f6c24eea": {
// 				"data_type": 'ALPHA',
// 				"name": 'ddfieldname3'
// 			},
// 		}
// 	}
// }

export const AddToDataDepot = ({updatedSelectedNode, setUpdatedSelectedNode, templateType}) => {
	//translation
	const { t } = useTranslation();
	const dispatch = useDispatch();

	//redux
	const parentScreen = useContext(ParentScreenCtx);
	const treeData = useSelector((state) => state.treeData);
	const dataDepots = treeData.dataDepots || {};
	const rightTree = treeData[parentScreen].rightTree;
	const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
	const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;

	//states
	const [loadedInitial, setLoadedInitial] = useState(false);
	const [enabled, setEnabled] = useState(false);

	const ddOptionsDefault = dataDepots ? 
		Object.values(dataDepots)
			.sort((a, b) => (a?.name && b?.name && typeof a?.name === "string" && typeof b?.name === "string") ? a.name.localeCompare(b.name) : -1)
			.map(o => o.name) 
		: [];
	const	[ddOptions, setDdOptions] = useState(ddOptionsDefault || {});
	const [dataDepotValue, setDataDepotValue] = useState('');
	const [dataDepotDisabled, setDataDepotDisabled] = useState(false);

	const [dataFieldOptions, setDataFieldOptions] = useState([]);
	const [dataFieldValue, setDataFieldValue] = useState('');
	const [dataFieldDisabled, setDataFieldDisabled]  = useState(false);

	const nodeFieldType = updatedSelectedNode?.FIELD ? updatedSelectedNode.FIELD.FIELDTYPE || "ALPHA" : updatedSelectedNode?.FIELDTYPE || "ALPHA";

	const debouncedInitialStateSet = useCallback(
		debounce(() => setInitialStates(), 250, true), []
	)

	useEffect(() => {
		//data depot list, change when dataDepots change
		setDdOptions(dataDepots ? 
			Object.values(dataDepots)
				.sort((a, b) => (a?.name && b?.name && typeof a?.name === "string" && typeof b?.name === "string") ? a.name.localeCompare(b.name) : -1)
				.map(o => o.name) 
			: [])
	}, [dataDepots]);

	useEffect(() => {
		//AddToDataDepot component was made with Outbound properties logic in mind. 
		// adding this logic here so Inbound properties can use this component.
		if (templateType === "inbound") setInitialStates();
		else debouncedInitialStateSet();
	}, [ddOptions]);

	//AddToDataDepot component was made with Outbound properties logic in mind. 
	// adding this logic here so Inbound properties can use this component.
	useEffect(() => {
		if (templateType === "inbound") setInitialStates();
	}, [selectedNode.key]);

	useEffect(() => {
		//on field change, update dataFieldOptions. 
		//if dataDepotValue is not included in the new array, set it to first option in array.

		if (nodeFieldType) {
			if (dataDepotValue) {
				let updatedFieldOptions = getSortedFieldsFromDD(dataDepotValue);
				let firstField = updatedFieldOptions[0] || "";

				setDataFieldOptions(updatedFieldOptions || [""]);
				if (updatedFieldOptions.length > 0) {
					if (dataFieldValue && updatedFieldOptions.includes(dataFieldValue)) {
						setDataFieldValue(dataFieldValue);
						modifyUpdatedNode({
							DATA_DEPOT_FIELD: dataFieldValue,
							DATA_DEPOT_FIELD_NAME: getFieldGuidByName(dataDepotValue, dataFieldValue)
						});
					} else {
						
						if (firstField) {
							setDataFieldValue(firstField);
							modifyUpdatedNode({
								DATA_DEPOT_FIELD: firstField,
								DATA_DEPOT_FIELD_NAME: getFieldGuidByName(dataDepotValue, firstField)
							});
						} else {
							setDataFieldValue("");
							setDataFieldDisabled(true);
							modifyUpdatedNode({
								DATA_DEPOT_FIELD: null,
								DATA_DEPOT_FIELD_NAME: null
							});
						}
					}
				} else {
					setDataFieldValue("");
					setDataFieldDisabled(true);
				}
			}
		}
	}, [nodeFieldType]);

	//return array of fields, given a data depot name
	const getSortedFieldsFromDD = (ddName) => {
		let sorted = [];
		Object.entries(dataDepots).forEach(([key, value]) => {
			if (value.name === ddName) {
				sorted = Object.values(value.fieldList)
					.filter((obj) => [nodeFieldType, "ALPHA"].includes(obj.data_type))
					.sort((a, b) => a.name.localeCompare(b.name))
					.map(o => o.name);
			}
		});
		return sorted;
	};

	const getDDuidByName = (ddName) => {
		let guid = "";
		Object.keys(dataDepots).forEach(key => {
			if (dataDepots[key].name === ddName) guid = key;
		});
		return guid;
	}

	//return guid string, given a data depot -> field name
	const getFieldGuidByName = (ddName, ddfName) => {
		let guid = "";
		let ddObj = dataDepots[getDDuidByName(ddName)];

		if (ddObj?.fieldList) {
			Object.keys(ddObj.fieldList).forEach(key => {
				if (ddObj.fieldList[key].name === ddfName) guid = key;
			});
		} 
		return guid;
	};

	const setInitialStates = () => {
		let sn = selectedNode;
		if (sn) {
			if ((sn.DATA_DEPOT && sn.DATA_DEPOT_FIELD) || (sn.FIELD?.DATA_DEPOT && sn.FIELD?.DATA_DEPOT_FIELD)) {
				setEnabled(true);
				
				let snDD = (sn.FIELD ? sn.FIELD.DATA_DEPOT : sn.DATA_DEPOT) || "";
				let snDF = (sn.FIELD ? sn.FIELD.DATA_DEPOT_FIELD : sn.DATA_DEPOT_FIELD) || "";
				let firstDD = ddOptions[0] || "";
				let fieldOptions = ddOptions.includes(snDD) ? getSortedFieldsFromDD(snDD) : getSortedFieldsFromDD(firstDD);
				let firstField = fieldOptions[0] || "";

				if (ddOptions.includes(snDD)) setDataDepotValue(snDD);
				else setDataDepotValue(firstDD);

				setDataFieldOptions(fieldOptions || []);

				if (fieldOptions.length > 0) {
					if (fieldOptions.includes(snDF)) {
						setDataFieldValue(snDF);
					} else {
						setDataFieldValue(firstField);
						if (!firstField) setDataFieldDisabled(true);
					}
				} else {
					setDataFieldValue("");
					setDataFieldDisabled(true);
				}
			} else {
				setDataDepotValue('');
				setDataFieldValue('');
				setEnabled(false);
			}
		}
	}

  const modifyUpdatedNode = (props, clearAll) => {
		if (clearAll) {
			if (updatedSelectedNode.FIELD) {
				setUpdatedSelectedNode(prev => {
          return { ...prev, FIELD: {
						...prev.FIELD, 
						"DATA_DEPOT": null, 
						"DATA_DEPOT_FIELD": null, 
						"DATA_DEPOT_FIELD_NAME": null, 
						"DATA_DEPOT_NAME": null, 
						"LOGGING_ON": null
					}}
        });
			} else {
				setUpdatedSelectedNode(prev => {
          return { 
						...prev,  
						"DATA_DEPOT": null, 
						"DATA_DEPOT_FIELD": null, 
						"DATA_DEPOT_FIELD_NAME": null, 
						"DATA_DEPOT_NAME": null, 
						"LOGGING_ON": null
					}
        });
			}
		} else {
			if (updatedSelectedNode.FIELD) {
				setUpdatedSelectedNode(prev => {
          return { ...prev, FIELD: {
						...prev.FIELD, 
						...props
					}}
        });
			} else {
				setUpdatedSelectedNode(prev => {
          return { 
						...prev,  
						...props
					}
        });
			}
		}
  }

	// given an array of prop/vals, store changes to current node in tData
	const saveToNode = (props, clearAll, tData, sNode) => {
    let newSelectedNode = cloneDeep(sNode);
    let updatedData = eachDeep(cloneDeep(tData), (node, key, parentArr, ctx) => {
      if (typeof node !== 'object') return;
      if (node?.nodeId && node.nodeId === selectedNode.nodeId) {
				if (clearAll) {
					["DATA_DEPOT", "DATA_DEPOT_FIELD", "DATA_DEPOT_FIELD_NAME", "DATA_DEPOT_NAME", "LOGGING_ON"].forEach(propName => {
						if (node[propName]) delete node[propName];
					})
				} else {
					props.forEach(obj => {
						if (obj.val === "") {
							if (node[obj.name]) delete node[obj.name];
						} else node[obj.name] = obj.val;
					})
				}
				newSelectedNode = {...node};
      }
    });

		//prevent dispatch for no reason
		if (isEqual(tData, updatedData)) return;

		//set selectedNode
		dispatch(
			treeActions.setRightTreeSelectedNode({
				screen: parentScreen,
				type: rightTree.currentTab,
				data: newSelectedNode,
			})
		);

    //set new trans data to updatedData.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  };

	//debounce saveToNode function to prevent multiple calls in quick succession
	const storeDropdownVals = useCallback(
    debounce((props, clearAll, tData, sNode) => saveToNode(props, clearAll, tData, sNode), 250, true), []
  );

	const handleEnabledChecked = (enabled) => {
		if (enabled) {
			if (ddOptions.length > 0) {
				if (dataDepotValue && dataFieldValue && ddOptions.includes(dataDepotValue)) {
					// dd and field values exist already, set them
					let fieldOptions = getSortedFieldsFromDD(dataDepotValue);
					if (fieldOptions.length === 0) fieldOptions = getSortedFieldsFromDD(ddOptions[0]);
					// if fieldOptions array is not exact same as fieldOptions, set new dataFieldOptions
					if (!isEqual(fieldOptions, dataFieldOptions)) setDataFieldOptions(fieldOptions);

					if (!fieldOptions.includes(dataFieldValue)) {
						//field value is not valid, set to first field in list
						let firstField = fieldOptions[0] || "";
						setDataFieldValue(firstField);

						if (firstField) {
							//if first field exists, store new values in redux
							modifyUpdatedNode({
								DATA_DEPOT_FIELD: dataFieldValue,
								DATA_DEPOT_FIELD_NAME: getFieldGuidByName(dataDepotValue, dataFieldValue)
							});
						} else {
							//if first field doesn't exist, clear the values
							modifyUpdatedNode({
								DATA_DEPOT_FIELD: null,
								DATA_DEPOT_FIELD_NAME: null
							});
						}
					} else {
						let firstDD = ddOptions[0] || "";
						let firstDDFields = firstDD ? getSortedFieldsFromDD(firstDD) : [];
						let firstFieldValue = firstDDFields.length > 0 ? firstDDFields[0] || "" : "";

						setDataDepotValue(firstDD);
						setDataFieldOptions(firstDDFields);
						setDataFieldValue(firstFieldValue);

						modifyUpdatedNode({
							DATA_DEPOT: firstDD,
							DATA_DEPOT_FIELD: firstFieldValue,
							DATA_DEPOT_FIELD_NAME: getFieldGuidByName(firstDD, firstFieldValue),
							DATA_DEPOT_NAME: getDDuidByName(firstDD),
							LOGGING_ON: null,
						});
					}
					if (dataDepotDisabled) setDataDepotDisabled(false);
					if (dataFieldDisabled) setDataFieldDisabled(false);
				} else {
					// no dataDepotValue or dataFieldValue, default to first values in list
					let firstDD = ddOptions[0] || "";
					let firstDDFields = firstDD ? getSortedFieldsFromDD(firstDD) : [];
					let firstFieldValue = firstDDFields.length > 0 ? firstDDFields[0] || "" : "";

					setDataDepotValue(firstDD);
					setDataFieldOptions(firstDDFields);
					setDataFieldValue(firstFieldValue);

					if (firstDD && firstFieldValue && firstDDFields.length > 0) {
						if (dataDepotDisabled) setDataDepotDisabled(false);
						if (dataFieldDisabled) setDataFieldDisabled(false);

						//if first dd and field values exist, store new values in redux
						modifyUpdatedNode({
							DATA_DEPOT: firstDD,
							DATA_DEPOT_FIELD: firstFieldValue,
							DATA_DEPOT_FIELD_NAME: getFieldGuidByName(firstDD, firstFieldValue),
							DATA_DEPOT_NAME: getDDuidByName(firstDD),
							LOGGING_ON: "TRUE",
						});
					} else {
						//if first dd and field values don't exist, change nothing and disable dropdowns
						if (!firstDD) setDataDepotDisabled(true);
						if (!firstFieldValue) setDataFieldDisabled(true);
					}
				}
			} else {
				//no data depots loaded. disable dropdowns and set values empty.
				setDataDepotDisabled(true);
				setDataFieldDisabled(true);
				setDataFieldOptions([]);
				setDataDepotValue('');
				setDataFieldValue('');
			}
		} else {
			//clear all values from node in redux
			modifyUpdatedNode({}, true);
		}
	}

	const handleDataDepotChange = (e, info) => {
		let value = info.props.value;

		//if value is the same, return to prevent unnecessary calls.
		if (dataDepotValue === value) return;

		let fieldOptions = getSortedFieldsFromDD(value);
		let firstField = fieldOptions[0] || "";

		if (value === "") {
			//this case should not happen, but if it does, reset data field states to prevent errors that could happen
			setDataFieldOptions([]);
			setDataFieldValue("");
			setDataFieldDisabled(true);
		} else {
			setDataFieldOptions(fieldOptions);
			setDataFieldValue(firstField);

			if (dataFieldDisabled) setDataFieldDisabled(false);

			modifyUpdatedNode({
				DATA_DEPOT: value,
				DATA_DEPOT_FIELD: firstField,
				DATA_DEPOT_FIELD_NAME: getFieldGuidByName(value, firstField),
				DATA_DEPOT_NAME: getDDuidByName(value), 
				LOGGING_ON: "TRUE",
			});
		}
	}

	//on field val change, store changes to node in tData
	const handleFieldChange = (e, info) => {
		let value = info.props.value;

		//if value is the same, return to prevent unnecessary calls.
		if (dataFieldValue === value) return;

		if (dataFieldDisabled) setDataFieldDisabled(false);

		modifyUpdatedNode({
			DATA_DEPOT_FIELD: value,
			DATA_DEPOT_FIELD_NAME: getFieldGuidByName(dataDepotValue, value)
		});
	}

	return (
		<div style={{marginTop: "10px"}}>
			<FormControlLabel
				sx={{ height: '30px' }}
				className={'width-fit-content'}
				label={t("BTN_ADD_TO_DATA_DEPOT")}
				control={
					<Checkbox
						name={"BTN_ADD_TO_DATA_DEPOT"}
						size={'small'}
						checked={enabled}
						onChange={(e, checked) => {
							setEnabled(checked);
							handleEnabledChecked(checked);
						}}
					/>
				}
			/>
			
			<div style={{ display: enabled ? "flex" : "none", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
				<FormControl
					style={{
						width: "unset",
						minWidth: "100px",
						flex: "1",
						// marginRight: "8px",
						// marginTop: "8px"
					}}
				>
					<InputLabel>{t('DATA_DEPOT')}</InputLabel>
					<Select
						size="small"
						MenuProps={{
							PaperProps: {
								sx: {
									maxHeight: 300,
								},
							},
						}}
						labelId={"obAddDataDepotLabel"}
						value={dataDepotValue}
						label={t('DATA_DEPOT')}
						onChange={(e, info) => {
							setDataDepotValue(info.props.value);
							handleDataDepotChange(e, info)
						}}
						disabled={dataDepotDisabled}
					>
						{ddOptions.map((str, i) => <MenuItem key={str + i} value={str}>{str}</MenuItem>) || ['']}
					</Select>
				</FormControl>
				
				<FormControl
					style={{
						width: "unset",
						minWidth: "100px",
						flex: "1",
						// marginTop: "8px"
					}}
				>
					<InputLabel>{t('DATA_FIELD')}</InputLabel>
					<Select
						size="small"
						MenuProps={{
							PaperProps: {
								sx: {
									maxHeight: 300,
								},
							},
						}}
						labelId={"obAddDataFieldLabel"}
						value={dataFieldValue}
						label={t('DATA_FIELD')}
						onChange={(e, info) => {
							setDataFieldValue(info.props.value);
							handleFieldChange(e, info);
						}}
						disabled={dataFieldDisabled}
					>
						{dataFieldOptions.map((str, i) => <MenuItem key={i} value={str}>{str}</MenuItem>)}
					</Select>
				</FormControl>
				
			</div>
		</div>
	)
}